import { useEffect, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button, DataGrid, SegmentedControl } from '@eo/shared';

import type { CapacityEntryRow } from './api/types';
import { ClientSummary } from './ClientSummary';
import { makeEntriesGridConfig } from './config/gridConfig';
import { DETAIL_PANE_LABELS, DETAIL_PANES, LOG_GRID_PREFIX } from './constants';
import { logFilters, parseDetailPane, selectionSearch } from './helpers';
import { MemberSummary } from './MemberSummary';
import type { DetailPane, SelectionDetailProps } from './types';

// The detail of whoever was picked on the page: a summary, and their time log
// only when asked for. It scrolls into view because it opens below the fold.
export function SelectionDetail({ data, selection, onQueryOptions }: SelectionDetailProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const pane = parseDetailPane(searchParams.get('pane') ?? undefined);
  const { kind, id } = selection;
  const logConfig = useMemo(
    () =>
      makeEntriesGridConfig(
        logFilters(data, data.report, { kind, id }, searchParams, onQueryOptions),
      ),
    [data, kind, id, searchParams, onQueryOptions],
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [selection.kind, selection.id]);

  const onPaneChange = (next: DetailPane) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'summary') params.delete('pane');
      else params.set('pane', next);
      for (const key of [...params.keys()]) {
        if (key.startsWith(LOG_GRID_PREFIX)) params.delete(key);
      }
      return params;
    });

  const onClose = () => setSearchParams(new URLSearchParams(selectionSearch(searchParams, null)));

  return (
    <div ref={ref} data-testid="selection-detail" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <SegmentedControl<DetailPane>
          options={DETAIL_PANES.map((value) => ({ value, label: DETAIL_PANE_LABELS[value] }))}
          value={pane}
          onChange={onPaneChange}
          ariaLabel="Detail view"
          className="w-fit"
        />
        <Button variant="ghost" size="icon" aria-label="Close detail" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>
      {pane === 'log' ? (
        <DataGrid<CapacityEntryRow>
          config={logConfig}
          rows={data.entryRows}
          pagination={data.entriesPagination}
        />
      ) : selection.kind === 'client' ? (
        <ClientSummary report={data.report} clientId={selection.id} />
      ) : (
        <MemberSummary report={data.report} staffId={selection.id} />
      )}
    </div>
  );
}
